import React, { useEffect, useState, useRef } from 'react';
import { Send, Mic, Paperclip, Smile, ArrowLeft } from 'lucide-react';
import { Message as MessageComponent } from './Message';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ChannelMessage, getMessages, sendChannelMessage, deleteMessage, subscribeToMessages } from '../../lib/chat';
import { RealtimeChannel } from '@supabase/supabase-js';

export function ChatRoom() {
  const navigate = useNavigate();
  const { channelId } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChannelMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const subscriptionRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!channelId) return;

    const loadMessages = async () => {
      try {
        const channelMessages = await getMessages(channelId);
        setMessages(channelMessages);
        setLoading(false);
      } catch (error) {
        console.error('Error loading messages:', error);
        setError('Fehler beim Laden der Nachrichten');
        setLoading(false);
      }
    };

    loadMessages();

    // Set up real-time subscription
    subscriptionRef.current = subscribeToMessages(channelId, (message, eventType) => {
      if (eventType === 'INSERT' && message.user_id !== user?.id) {
        setMessages((prev) => [...prev, message]);
      } else if (eventType === 'DELETE') {
        setMessages((prev) => prev.filter((m) => m.id !== message.id));
      }
    });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [channelId, user?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!channelId || !newMessage.trim() || !user) return;

    try {
      const sentMessage = await sendChannelMessage(channelId, newMessage.trim());
      setMessages((prev) => [...prev, sentMessage]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Fehler beim Senden der Nachricht');
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await deleteMessage(messageId);
      setMessages((prev) => prev.filter((message) => message.id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
      setError('Fehler beim Löschen der Nachricht');
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-text-secondary">Laden...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-screen">
      <div className="p-4 border-b border-divider">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="md:hidden p-2 hover:bg-background-secondary rounded-full"
          >
            <ArrowLeft size={20} />
          </button>
          <span className="text-xl font-bold"># {channelId || 'allgemein'}</span>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/50 text-red-500">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-2 sm:p-4 space-y-4">
        {messages.map((message) => (
          <MessageComponent
            key={message.id}
            id={message.id}
            user={{
              id: message.user_id,
              name: message.profiles?.username || 'Unbekannter Benutzer',
              image: message.profiles?.avatar_url,
              role: message.profiles?.artist_types[0] || 'Künstler'
            }}
            content={message.content}
            timestamp={new Date(message.created_at).toLocaleTimeString('de-DE', {
              hour: '2-digit',
              minute: '2-digit'
            })}
            onDelete={handleDeleteMessage}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className="p-2 sm:p-4 border-t border-divider">
        <div className="flex items-center gap-2">
          <button 
            type="button"
            className="p-2 text-text-secondary hover:text-text-primary hidden sm:block"
          >
            <Paperclip size={20} />
          </button>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Nachricht schreiben..."
            className="flex-1 bg-background-secondary rounded-full px-4 py-2 focus:outline-none focus:ring-1 focus:ring-accent-primary"
          />
          <button 
            type="button"
            className="p-2 text-text-secondary hover:text-text-primary hidden sm:block"
          >
            <Smile size={20} />
          </button>
          <button 
            type="button"
            className="p-2 text-text-secondary hover:text-text-primary hidden sm:block"
          >
            <Mic size={20} />
          </button>
          <button 
            type="submit"
            disabled={!newMessage.trim()}
            className="p-2 text-accent-primary hover:text-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={20} />
          </button>
        </div>
      </form>
    </div>
  );
}